import React, { useState } from 'react';
import styles from './style.module.css';
import Typography from '../../../components/Typography/Typography';
import FlatButtons from '../../../components/FlatButton/FlatButton';
import { useChangeUserPasswordMutation } from '../../../redux/services/userApi';

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange }) => {
  return (
    <label className={styles.toggleSwitch}>
      <input 
        type="checkbox" 
        checked={checked} 
        placeholder='toggle'
        onChange={(e) => onChange(e.target.checked)} 
        className={styles.toggleInput} 
      />
      <span className={styles.slider}></span>
    </label>
  );
};

export const SellerSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'security' | 'notification'>('security');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [is2faEnabled, setIs2faEnabled] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [emailAlerts, setEmailAlerts] = useState(true);
  
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const [changePassword, { isLoading }] = useChangeUserPasswordMutation();

  const validatePasswordForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (currentPassword && newPassword && currentPassword === newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (activeTab === 'security') {
      if (!validatePasswordForm()) {
        return;
      }

      try {
        const response = await changePassword({
          old_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }).unwrap();

        alert('Password changed successfully!');
        
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setErrors({});
      } catch (error: any) {
        console.error('Failed to change password:', error);
        
        if (error?.data?.detail) {
          if (typeof error.data.detail === 'string') {
            alert(`Error: ${error.data.detail}`);
          } else if (Array.isArray(error.data.detail)) {
            const errorMessage = error.data.detail
              .map((err: any) => err.msg)
              .join(', ');
            alert(`Error: ${errorMessage}`);
          }
        } else if (error?.status === 401) {
          alert('Current password is incorrect');
        } else {
          alert('Failed to change password. Please try again.');
        }
      }
    } else {
      console.log('Saving Notification Settings:', { pushNotifications, emailAlerts });
      alert('Notification settings saved!');
    }
  };

  const clearError = (field: keyof typeof errors) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.tabContainer}>
        <FlatButtons 
          className={`${styles.tabButton} ${activeTab === 'security' ? styles.activeTab : ''}`} 
          onClick={() => setActiveTab('security')}
        >
          <Typography
            variant="bodyRegular"
            fontFamily='satoshi'
            className={activeTab === 'security' ? styles.activeTabText : styles.inactiveTabText}
          >
            Security
          </Typography>
        </FlatButtons>
        <FlatButtons
          className={`${styles.tabButton} ${activeTab === 'notification' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('notification')}
        >
          <Typography
            variant="bodyRegular"
            fontFamily='satoshi'
            className={activeTab === 'notification' ? styles.activeTabText : styles.inactiveTabText}
          >
            Notification
          </Typography>
        </FlatButtons>
      </div>

      <div className={styles.settingsBox}>
        {activeTab === 'security' && (
          <>
            <div className={styles.formSection}>
              <Typography 
                variant="heading4"
                fontFamily='satoshi'
                className={styles.sectionTitle}
              >
                Change Password
              </Typography>
              
              <div className={styles.formGroup}>
                <label htmlFor="current-password">
                  <Typography 
                    variant="bodyRegular"
                    fontFamily='satoshi'
                    className={styles.labelText}
                  >
                    Current Password
                  </Typography>
                </label>
                <input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    clearError('currentPassword');
                  }}
                  className={`${styles.formInput} ${errors.currentPassword ? styles.inputError : ''}`}
                  placeholder="Enter current password"
                />
                {errors.currentPassword && (
                  <Typography 
                    variant="bodySmall"
                    fontFamily='satoshi'
                    className={styles.errorText}
                  >
                    {errors.currentPassword}
                  </Typography>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="new-password">
                  <Typography 
                    variant="bodyRegular"
                    fontFamily='satoshi'
                    className={styles.labelText}
                  >
                    New Password
                  </Typography>
                </label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    clearError('newPassword');
                  }}
                  className={`${styles.formInput} ${errors.newPassword ? styles.inputError : ''}`}
                  placeholder="Enter new password"
                />
                {errors.newPassword && (
                  <Typography 
                    variant="bodySmall"
                    fontFamily='satoshi'
                    className={styles.errorText}
                  >
                    {errors.newPassword}
                  </Typography>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="confirm-password">
                  <Typography 
                    variant="bodyRegular"
                    fontFamily='satoshi'
                    className={styles.labelText}
                  >
                    Confirm New Password
                  </Typography>
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    clearError('confirmPassword');
                  }}
                  className={`${styles.formInput} ${errors.confirmPassword ? styles.inputError : ''}`}
                  placeholder="Confirm new password"
                />
                {errors.confirmPassword && (
                  <Typography 
                    variant="bodySmall"
                    fontFamily='satoshi'
                    className={styles.errorText}
                  >
                    {errors.confirmPassword}
                  </Typography>
                )}
              </div>
            </div>

            <div className={`${styles.toggleSection} ${styles.lastSection}`}>
              <div>
                <Typography 
                  variant="heading4"
                  fontFamily='satoshi' 
                  className={styles.toggleTitle}
                >
                  Two Factor Authentication
                </Typography>
                <Typography
                  variant="bodyRegular"
                  fontFamily='satoshi' 
                  className={styles.toggleDescription}
                >
                  Add an extra layer of security to prevent theft
                </Typography>
              </div>
              <ToggleSwitch checked={is2faEnabled} onChange={setIs2faEnabled} />
            </div>
          </>
        )}

        {activeTab === 'notification' && (
          <>
            <Typography 
              variant="heading4"
              fontFamily='satoshi'
              className={styles.sectionTitle}
            >
              Notification Setting
            </Typography>
            <div className={styles.toggleSection}>
              <div>
                <Typography 
                  variant="heading6"
                  fontFamily='satoshi'
                  className={styles.toggleTitle}
                >
                  Push Notification
                </Typography>
                <Typography
                  variant="bodyRegular"
                  fontFamily='satoshi' 
                  className={styles.toggleDescription}
                >
                  Instant updates on sales & activity
                </Typography>
              </div>
              <ToggleSwitch checked={pushNotifications} onChange={setPushNotifications} />
            </div>
            <div className={`${styles.toggleSection} ${styles.lastSection}`}>
              <div>
                <Typography 
                  variant="heading6"
                  fontFamily='satoshi' 
                  className={styles.toggleTitle}
                >
                  Email Alerts
                </Typography>
                <Typography
                  variant="bodyRegular"
                  fontFamily='satoshi' 
                  className={styles.toggleDescription}
                >
                  Reports and reminders via email
                </Typography>
              </div>
              <ToggleSwitch checked={emailAlerts} onChange={setEmailAlerts} />
            </div>
          </>
        )}

        <div className={styles.saveButtonContainer}>
          <FlatButtons 
            onClick={handleSave} 
            className={styles.saveButton}
            disabled={isLoading}
          >
            <Typography 
              variant="buttonText"
              fontFamily='satoshi'
              className={styles.saveButtonText}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Typography>
          </FlatButtons>
        </div>
      </div>
    </div>
  );
};